import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutGrid, FolderKanban, LifeBuoy, Cpu, Calculator, FileText, LogOut, Shield, ShieldAlert,
} from "lucide-react";
import Logo from "@/components/common/Logo";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

export default function AppLayout() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const links = [
    { to: "/dashboard",  Icon: LayoutGrid,    label: t("nav.dashboard"),  test: "nav-dashboard-link" },
    { to: "/projects",   Icon: FolderKanban,  label: t("nav.projects"),   test: "nav-projects-link" },
    { to: "/tickets",    Icon: LifeBuoy,      label: t("nav.tickets"),    test: "nav-tickets-link" },
    { to: "/hardware",   Icon: Cpu,           label: t("nav.hardware"),   test: "nav-hardware-link" },
    { to: "/calculator", Icon: Calculator,    label: t("nav.calculator"), test: "nav-calculator-link" },
    { to: "/documents",  Icon: FileText,      label: t("nav.documents"),  test: "nav-documents-link" },
    { to: "/renewals",   Icon: ShieldAlert,   label: t("nav.renewals"),   test: "nav-renewals-link" },
  ];

  const roleLabel = {
    admin: "ADMIN",
    staff: "STAFF",
    client_premium: "PREMIUM",
    client_standard: "STANDARD",
  }[user?.role] || "CLIENT";

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-border bg-card/60 backdrop-blur-sm flex flex-col" data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-border">
          <button onClick={() => nav("/dashboard")} className="block">
            <Logo size={28} />
          </button>
        </div>
        <nav className="flex-1 py-4 px-2 space-y-0.5">
          {links.map(({ to, Icon, label, test }) => (
            <NavLink
              key={to} to={to} data-testid={test}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary border-l-2 border-primary pl-[10px]"
                    : "text-secondary-foreground/70 hover:text-primary hover:bg-secondary/50"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/25 flex items-center justify-center">
              <Shield className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-xs truncate text-white" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[9px] font-mono tracking-[0.22em] text-primary" data-testid="sidebar-user-role">{roleLabel}</div>
            </div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            data-testid="sidebar-logout-button"
            className="w-full inline-flex items-center justify-center gap-2 px-3 h-8 rounded-sm border border-border hover:border-destructive/50 hover:text-destructive text-xs font-mono tracking-widest uppercase text-muted-foreground transition-colors"
          >
            <LogOut className="w-3 h-3" /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border px-6 flex items-center justify-between bg-background/70 backdrop-blur-md" data-testid="app-topbar">
          <div className="flex items-center gap-3">
            <div className="pulse-dot w-1.5 h-1.5 rounded-full bg-[#00E676]" />
            <span className="text-[10px] font-mono tracking-[0.28em] text-muted-foreground uppercase">Online · Operational</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
          </div>
        </header>
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
